import { useMemo } from 'react';
import { ErrorWrapper } from '@rsos/base-components/form/ErrorsWrapper';
import { Select } from '@rsos/components/capstone/base';
import type { NorfolkSouthernTrainSelection } from '@rsos/reduxDataStore/src/features/norfolkSouthern/norfolkSouthernSlice';
import {
  formatTrainLabel,
  originDateFromTrainId,
} from '../../../../../utils/norfolkSouthern/trains';
import { useSelectStyles } from '../TowPro/shared';
import FieldLabel from './FieldLabel';
import styles from './NorfolkSouthernForm.module.css';
import {
  visibleFieldError,
  type NorfolkSouthernFormik,
  type SelectOption,
} from './formFieldUtils';

const NO_TRAINS_MESSAGE =
  'No Norfolk Southern trains are in your jurisdiction right now. Trains appear here as their positions arrive, or select one on the map.';

interface TrainOption extends SelectOption {
  trainId: string;
}

/** A train matches typed input on its symbol or its train ID. */
const filterTrainOption = (
  option: { data: TrainOption },
  input: string,
): boolean => {
  const typed = input.trim().toLowerCase();
  return (
    option.data.label.toLowerCase().includes(typed) ||
    option.data.trainId.toLowerCase().includes(typed)
  );
};

interface TrainFieldProps {
  formik: NorfolkSouthernFormik;
  /** The trains the user can pick from. */
  trains: NorfolkSouthernTrainSelection[];
  /** Called with the train the user picks. */
  onPickTrain: (train: NorfolkSouthernTrainSelection | null) => void;
}

/** The train picker, labelled by symbol. Picking a train fills the origin date from its ID. */
function TrainField({
  formik,
  trains,
  onPickTrain,
}: TrainFieldProps): JSX.Element {
  const trainOptions = useMemo(
    (): TrainOption[] =>
      trains.map(train => ({
        value: train.externalId,
        label: formatTrainLabel(train),
        trainId: train.trainId,
      })),
    [trains],
  );
  const selectedOption =
    trainOptions.find(option => option.value === formik.values.externalId) ??
    null;
  const error = visibleFieldError(formik, 'externalId');
  const selectStyles = useSelectStyles(error);

  return (
    <div className={styles.field}>
      <FieldLabel required>Train Symbol</FieldLabel>
      <Select
        name="externalId"
        classNamePrefix="norfolk-southern-train"
        options={trainOptions}
        placeholder="Select a train"
        isClearable={false}
        isSearchable
        filterOption={filterTrainOption}
        isDisabled={trainOptions.length === 0}
        noOptionsMessage={() => 'No trains in your jurisdiction'}
        onChange={(option: TrainOption | null) => {
          // Picking the train already chosen keeps a corrected origin date.
          if (option?.value === formik.values.externalId) {
            return;
          }
          const train =
            trains.find(t => t.externalId === option?.value) ?? null;
          onPickTrain(train);
          // The origin date follows the train it was read from.
          formik.setValues(
            {
              ...formik.values,
              externalId: option?.value ?? '',
              originDate: train ? originDateFromTrainId(train.trainId) : '',
            },
            true,
          );
          formik.setFieldTouched('externalId', true, false);
        }}
        onBlur={() => formik.setFieldTouched('externalId', true)}
        value={selectedOption}
        styles={selectStyles}
      />
      {error && <ErrorWrapper error={error} />}
      {trainOptions.length === 0 && (
        <div
          className={styles.helperText}
          data-name="norfolk-southern-no-trains"
        >
          {NO_TRAINS_MESSAGE}
        </div>
      )}
    </div>
  );
}

export default TrainField;
